<script setup>
const config = useRuntimeConfig()
const mapType = ref('major_city')
const { data: thumbnailList } = await useFetch(
  computed(() => `${config.public.siteDataDomain}/site/onebox/livecam/${mapType.value}/point_list.json`)
)
</script>

<template>
  <section class="livecamMap">
    <div class="head">
      <CommonPageTitle title="全国のライブカメラ" />
    </div>
    <div class="map">
      <figure>
        <img
          src="https://gvs.weathernews.jp/onebox/img/livecam/map/map.webp"
          alt=""
        >
        <svg
          class="map__svg"
          viewBox="0 0 1510 1241"
        >
          <NuxtLink
            v-for="area in areaToPrefTable"
            :key="area.name"
            :to="useCdnUrl(`/${area.key}/`)"
            class="map__polygon"
            external
          >
            <polygon
              :points="mapPolygon[area.key]"
              class="map__polygon"
            />
          </NuxtLink>
        </svg>
      </figure>
      <div class="map__select">
        <select v-model="mapType">
          <option value="major_city">
            主要都市
          </option>
          <option value="focus_point">
            注目地点
          </option>
        </select>
      </div>
      <div v-if="mapType === 'major_city'">
        <NuxtLink
          v-for="(item, key) in thumbnailList.list "
          :key="key"
          :to="useCdnUrl(`${getAreaPath(item.pref.toLowerCase())}${item.camid}/`)"
          class="map__link"
          external
          :data-area-major="key"
        >
          <p class="pref__text">{{ getFullPrefectureName(item.pref.toLowerCase()) }}</p>
          <figure>
            <img
              :src="item.image"
              alt=""
            >
          </figure>
        </NuxtLink>
      </div>
      <div v-if="mapType === 'focus_point'">
        <NuxtLink
          v-for="(item, key) in thumbnailList.list "
          :key="key"
          :to="useCdnUrl(`${getAreaPath(item.pref.toLowerCase())}${item.camid}/`)"
          class="map__link"
          external
          :data-area-focus="key"
        >
          <p>{{ item.name }}</p>
          <figure>
            <img
              :src="item.image"
              alt=""
            >
          </figure>
        </NuxtLink>
      </div>
    </div>
    <div class="button">
      <CommonMoreButton
        text="ライブカメラをもっと見る"
        direction="left"
        col="dark-blue"
        :link="useCdnUrl('/zoom-map/')"
      />
    </div>
  </section>
</template>

<style lang="scss" scoped>
.livecamMap {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .map {
    position: relative;
    figure {
      position: relative;
      width: 100%;
      aspect-ratio: 1510 / 1241;
      img {
        width: 100%;
      }
      .map__svg {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
      .map__polygon {
        fill: transparent;
        cursor: pointer;
        &:hover {
          fill: #eef1f8;
          opacity: 0.6;
        }
      }
    }
    &__select {
      position: absolute;
      bottom: max(5px, 1.33vw);
      right: max(5px, 1.33vw);
      border-radius: max(3px, 0.8vw);
      background-color: #FFF;
      box-shadow: 0 max(1px, 0.27vw) max(2px, 0.53vw) 0 rgb(0 0 0 / .25);
      select {
        padding: max(5px, 1.33vw) max(22px, 5.87vw) max(5px, 1.33vw) max(7px, 1.87vw);
        color: rgb(51 51 51 / .7);
        font-size: max(12px, 3.2vw);
        font-weight: 400;
        line-height: 1em;
        outline: none;
      }
      &::before {
        position: absolute;
        top: 50%;
        right: max(7px, 1.87vw);
        width: max(6px, 1.6vw);
        height: max(9px, 2.4vw);
        transform: rotate(90deg) translateX(-50%);
        background-image: url('https://gvs.weathernews.jp/onebox/img/common/icon_arrow_scroll.svg');
        background-position: center;
        background-repeat: no-repeat;
        background-size: cover;
        content: "";
      }
    }
    &__link {
      position: absolute;
      display: flex;
      flex-direction: column;
      gap: max(5px, 1.33vw);
      padding: max(4px, 1.07vw) max(2px, 0.53vw) max(2px, 0.53vw);
      border-radius: max(2px, 0.53vw);
      border: 1px solid #DFE4F0;
      background-color: #FFF;
      box-shadow: 0 max(4px, 1.07vw) max(4px, 1.07vw) 0 rgb(0 0 0 / .25);
      max-width: 94px;
      .pref__text {
        letter-spacing: 0.3em;
      }
      p {
        color: #1F59BA;
        font-size: max(10px, 2vw);
        width: max(40px, 10vw);
        font-weight: 600;
        line-height: 1em;
        text-align: center;
        text-decoration: underline;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      figure {
        width: max(40px, 10vw);
        height: max(40px, 10vw);
        border-radius: max(3px, 0.8vw);
        overflow: hidden;
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          vertical-align: bottom;
        }
      }
      &[data-area-major="0"] {
        top: 3%;
        left: 50%;
      }
      &[data-area-major="1"] {
        top: 35%;
        left: 74%;
      }
      &[data-area-major="2"] {
        top: 63%;
        left: 69%;
      }
      &[data-area-major="3"] {
        top: 30%;
        left: 50%;
      }
      &[data-area-major="4"] {
        top: 74%;
        left: 49%;
      }
      &[data-area-major="5"] {
        top: 40%;
        left: 35%;
      }
      &[data-area-major="6"] {
        top: 45%;
        left: 19%;
      }
      &[data-area-major="7"] {
        bottom: 2%;
        left: 29%;
      }
      &[data-area-major="8"] {
        top: 52%;
        left: 4%;
      }
      &[data-area-major="9"] {
        top: 9%;
        left: 28%;
      }
      &[data-area-focus="0"] {
        top: 3%;
        left: 50%;
      }
      &[data-area-focus="1"] {
        top: 30%;
        left: 74%;
      }
      &[data-area-focus="2"] {
        top: 54%;
        left: 75%;
      }
      &[data-area-focus="3"] {
        top: 72%;
        left: 60%;
      }
      &[data-area-focus="4"] {
        top: 75%;
        left: 45%;
      }
      &[data-area-focus="5"] {
        top: 34%;
        left: 46%;
      }
      &[data-area-focus="6"] {
        bottom: 2%;
        left: 28%;
      }
      &[data-area-focus="7"] {
        top: 46%;
        left: 27%;
      }
      &[data-area-focus="8"] {
        top: 56%;
        left: 6%;
      }
      &[data-area-focus="9"] {
        top: 9%;
        left: 28%;
      }
    }
  }
  .button {
    margin-left: auto;
  }
  @media (any-hover: hover) {
    .map {
      &__select {
        transition: .2s ease-in-out;
        cursor: pointer;
        &:hover {
          opacity: .6;
        }
      }
      &__link {
        transition: .2s ease-in-out;
        &:hover {
          opacity: .6;
        }
      }
    }
  }
}
@media screen and (min-width: 830px) {
  .livecamMap {
    .map {
      &__select {
        bottom: min(0.65vw, 7px);
        right: min(0.93vw, 10px);
        border-radius: min(0.56vw, 6px);
        box-shadow: 0 min(0.19vw, 2px) min(0.37vw, 4px) 0 rgb(0 0 0 / .25);
        select {
          padding: min(1.4vw, 15px) min(3.74vw, 40px) min(1.4vw, 15px) min(1.4vw, 15px);
          font-size: min(1.5vw, 16px);
        }
        &::before {
          right: min(1.4vw, 15px);
          width: min(0.93vw, 10px);
          height: min(1.4vw, 15px);
        }
      }
      &__link {
        gap: min(0.47vw, 5px);
        padding: min(0.75vw, 8px) min(0.47vw, 5px) min(0.47vw, 5px);
        border-radius: min(0.56vw, 6px);
        box-shadow: 0 min(0.37vw, 4px) min(0.37vw, 4px) 0 rgb(0 0 0 / .25);
        max-width: 92px;
        p {
          font-size: min(1.12vw, 12px);
          max-width: min(8.41vw, 80px);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        figure {
          width: min(8.41vw, 80px);
          aspect-ratio: 4 / 3;
          height: auto;
          border-radius: min(0.56vw, 6px);
        }
        &[data-area-major="0"] {
          top: 7%;
          left: 50%;
        }
        &[data-area-major="1"] {
          top: 30%;
          left: 74%;
        }
        &[data-area-major="2"] {
          top: 55%;
          left: 75%;
        }
        &[data-area-major="3"] {
          top: 38%;
          left: 48%;
        }
        &[data-area-major="4"] {
          top: 74%;
          left: 58%;
        }
        &[data-area-major="5"] {
          top: 78%;
          left: 41%;
        }
        &[data-area-major="6"] {
          top: 45%;
          left: 23%;
        }
        &[data-area-major="7"] {
          bottom: 5%;
          left: 23%;
        }
        &[data-area-major="8"] {
          top: 58%;
          left: 2%;
        }
        &[data-area-major="9"] {
          top: 9%;
          left: 28%;
        }
        &[data-area-focus="0"] {
          top: 3%;
          left: 50%;
        }
        &[data-area-focus="1"] {
          top: 30%;
          left: 73%;
        }
        &[data-area-focus="2"] {
          top: 52%;
          left: 75%;
        }
        &[data-area-focus="3"] {
          top: 73%;
          left: 60%;
        }
        &[data-area-focus="4"] {
          top: 75%;
          left: 42%;
        }
        &[data-area-focus="5"] {
          top: 34%;
          left: 46%;
        }
        &[data-area-focus="6"] {
          bottom: 2%;
          left: 24%;
        }
        &[data-area-focus="7"] {
          top: 46%;
          left: 27%;
        }
        &[data-area-focus="8"] {
          top: 56%;
          left: 6%;
        }
        &[data-area-focus="9"] {
          top: 9%;
          left: 28%;
        }
      }
    }
  }
}
</style>
